import React, { useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ClientLayout from '../../../components/layout/ClientLayout';
import Spinner from '../../../components/ui/Spinner';
import Alert from '../../../components/ui/Alert';
import { useAuth } from '../../../hooks/useAuth';
import { ITransacao as Transacao } from '../../../types/firestore.types';
import { useFirestoreQuery } from '../../../hooks/useFirestoreQuery';
import { formatarMoeda } from '@/utils/currencyUtils';
import {
  FaClock,
  FaStore,
  FaCalendarAlt,
  FaReceipt,
  FaFilter,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaMoneyBillWave,
  FaCalendar
} from 'react-icons/fa';

// 1. Constantes movidas para fora do componente
const UNIDADES = [
  { unidade_id: '1', label: 'Mr. Tugas Aeroporto', icone: '' },
  { unidade_id: '2', label: 'Mr. Tugas Cervejaria', icone: '' },
  { unidade_id: '3', label: 'Mr. Tugas Jardim Norte', icone: '' }
] as const;

const STATUS_CONFIG = {
  'pending': {
    label: 'Pendente',
    gradient: 'from-yellow-400 to-amber-500',
    bgGradient: 'from-yellow-500/15 to-amber-500/10',
    borderColor: 'border-primary/20',
    textColor: 'text-yellow-300',
    icon: '⏳',
    descricao: 'Aguardando processamento'
  },
  'completed': {
    label: 'Concluída',
    gradient: 'from-green-400 to-emerald-500',
    bgGradient: 'from-green-500/15 to-emerald-500/10',
    borderColor: 'border-primary/20',
    textColor: 'text-green-300',
    icon: '✅',
    descricao: 'Transação finalizada com sucesso'
  },
  'confirmed': {
    label: 'Concluída',
    gradient: 'from-green-400 to-emerald-500',
    bgGradient: 'from-green-500/15 to-emerald-500/10',
    borderColor: 'border-primary/20',
    textColor: 'text-green-300',
    icon: '✅',
    descricao: 'Transação finalizada com sucesso'
  },
  'expired': {
    label: 'Expirada',
    gradient: 'from-red-400 to-rose-500',
    bgGradient: 'from-red-500/15 to-rose-500/10',
    borderColor: 'border-primary/20',
    textColor: 'text-red-300',
    icon: <FaClock className="w-3 h-3" />,
    descricao: 'Transação expirou'
  },
  'failed': {
    label: 'Falhou',
    gradient: 'from-red-400 to-rose-500',
    bgGradient: 'from-red-500/15 to-rose-500/10',
    borderColor: 'border-primary/20',
    textColor: 'text-red-300',
    icon: '❌',
    descricao: 'Transação falhou'
  },
  'cancelled': {
    label: 'Cancelada',
    gradient: 'from-gray-400 to-slate-500',
    bgGradient: 'from-gray-500/15 to-slate-500/10',
    borderColor: 'border-primary/20',
    textColor: 'text-gray-300',
    icon: '🚫',
    descricao: 'Transação cancelada'
  },
  'validating': {
    label: 'Validando',
    gradient: 'from-blue-400 to-cyan-500',
    bgGradient: 'from-blue-500/15 to-cyan-500/10',
    borderColor: 'border-primary/20',
    textColor: 'text-blue-300',
    icon: '🔍',
    descricao: 'Pagamento em validação'
  },
  'pendente_confirmacao': {
    label: 'Pendente Confirmação',
    gradient: 'from-orange-400 to-amber-500',
    bgGradient: 'from-orange-500/15 to-amber-500/10',
    borderColor: 'border-primary/20',
    textColor: 'text-orange-300',
    icon: '📋',
    descricao: 'Aguardando confirmação'
  }
} as const;

const TRANSACTION_TYPES = {
  pix_payment: {
    label: 'Recarga PIX',
    icon: '💰',
    color: 'from-blue-400 to-cyan-500',
    badge: 'PIX'
  },
  cashback: {
    label: 'Cashback',
    icon: '🎁',
    color: 'from-green-400 to-emerald-500',
    badge: 'BÔNUS'
  },
  debito: {
    label: 'Pagamento',
    icon: '💳',
    color: 'from-purple-400 to-pink-500',
    badge: null
  },
  credito: {
    label: 'Crédito',
    icon: '📈',
    color: 'from-green-400 to-emerald-500',
    badge: null
  }
} as const;

// 2. Opções de ordenação
type OrdenacaoCampo = 'data' | 'valor' | 'tipo';
type OrdenacaoDirecao = 'asc' | 'desc';

const OPCOES_ORDENACAO = [
  { value: 'data-desc', label: 'Mais Recentes', icon: FaCalendar },
  { value: 'data-asc', label: 'Mais Antigas', icon: FaCalendar },
  { value: 'valor-desc', label: 'Maior Valor', icon: FaMoneyBillWave },
  { value: 'valor-asc', label: 'Menor Valor', icon: FaMoneyBillWave },
  { value: 'tipo-desc', label: 'Tipo (A-Z)', icon: FaSort },
  { value: 'tipo-asc', label: 'Tipo (Z-A)', icon: FaSort },
] as const;

// 3. Utilitários movidos para fora com memoização
const formatarDataHora = (timestamp: any): string => {
  if (!timestamp) return 'N/A';

  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Data inválida';

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return 'Erro na data';
  }
};

const formatarDataRelativa = (timestamp: any): string => {
  if (!timestamp) return 'N/A';

  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Data inválida';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `Há ${diffMinutes} min`;
    if (diffHours < 24) return `Há ${diffHours} h`;

    return formatarDataHora(timestamp);
  } catch (error) {
    return formatarDataHora(timestamp);
  }
};

// 4. Componente TransactionCard otimizado com React.memo
const TransactionCard = React.memo<{ transacao: Transacao; index: number }>(({ transacao, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  const {
    valorSanitizado,
    tipoOperacaoSanitizado,
    statusConfig,
    transactionType,
    unidade
  } = useMemo(() => {
    const valorSanitizado = typeof transacao.valor === 'number' ? transacao.valor : 0;
    const tipoOperacaoSanitizado = ['credito', 'debito'].includes(transacao.tipo_operacao)
      ? transacao.tipo_operacao
      : 'credito';

    const statusConfig = STATUS_CONFIG[transacao.status as keyof typeof STATUS_CONFIG] || {
      label: transacao.status,
      gradient: 'from-gray-400 to-slate-500',
      bgGradient: 'from-gray-500/15 to-slate-500/10',
      borderColor: 'border-primary/20',
      textColor: 'text-gray-300',
      icon: '❓',
      descricao: 'Status desconhecido'
    };

    let transactionType;
    if (transacao.tipo === 'pix_payment') {
      transactionType = TRANSACTION_TYPES.pix_payment;
    } else if (transacao.tipo === 'cashback') {
      transactionType = TRANSACTION_TYPES.cashback;
    } else if (transacao.tipo_operacao === 'debito') {
      transactionType = TRANSACTION_TYPES.debito;
    } else {
      transactionType = TRANSACTION_TYPES.credito;
    }

    const unidade = UNIDADES.find(u => u.unidade_id === transacao.unidade_id);

    return {
      valorSanitizado,
      tipoOperacaoSanitizado,
      statusConfig,
      transactionType,
      unidade
    };
  }, [transacao]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: Math.min(index * 0.1, 0.5), // Limita o delay máximo
        type: "spring",
        stiffness: 100
      }}
      whileHover={{
        y: -5,
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      className="relative group cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >

      <div className="absolute inset-0 z-0">
        <img
          src="/Background-secao2.png"
          alt="Background"
          className="w-full md:h-[90%] h-full object-cover rounded-xl"
        />
        <div className="absolute inset-0 bg-black/10"></div>
      </div>

      {/* Efeito de brilho no hover */}
      <div className={`absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-500`} />

      <div className={`relative rounded-xl border-2 border-primary/20 p-6 duration-300 group-hover:border-opacity-60 overflow-hidden`}>

        {/* Header da transação */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl  flex items-center justify-center text-xl shadow-lg border border-primary/20`}>
              {transactionType.icon}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-white mb-1 truncate">{transactionType.label}</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-1 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${statusConfig.gradient} text-white shadow-md flex items-center gap-1 border border-primary/20`}>
                  <span className="text-xs">{statusConfig.icon}</span>
                  {statusConfig.label}
                </span>
                {transactionType.badge && (
                  <span className="px-1 py-1 rounded-full text-xs bg-blue-500/20 text-primary border border-primary/20">
                    {transactionType.badge}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="text-right flex-shrink-0 ml-4">
            <motion.div
              animate={{ scale: isHovered ? 1.1 : 1 }}
              className={`text-2xl font-black ${tipoOperacaoSanitizado === 'credito' ? 'text-green-400' : 'text-red-400'}`}
            >
              {tipoOperacaoSanitizado === 'credito' ? '+' : '-'}{formatarMoeda(valorSanitizado).replace('R$', 'TC')}
            </motion.div>
            <div className="text-slate-400 text-sm mt-1 flex items-center justify-end gap-1">
              <span className={`w-2 h-2 rounded-full ${tipoOperacaoSanitizado === 'credito' ? 'bg-green-400' : 'bg-red-400'}`}></span>
              {tipoOperacaoSanitizado.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Detalhes da transação */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex flex-col items-center gap-3 rounded-xl p-3 border border-primary/20">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border border-primary/20">
              <FaStore className="w-3 h-3 text-slate-300" />
            </div>
            <div className="min-w-0">
              <div className="text-slate-400 text-xs">Unidade</div>
              <div className="text-white font-medium flex items-center gap-2 truncate">
                <span className="text-sm">{unidade?.icone}</span>
                {unidade?.label || "Tugas Coins"}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-3 bg-background rounded-xl p-3 border border-primary/20">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border border-primary/20">
              <FaCalendarAlt className="w-3 h-3 text-slate-300" />
            </div>
            <div className="min-w-0">
              <div className="text-slate-400 text-xs">Data/Hora</div>
              <div className="text-white font-medium truncate">{formatarDataHora(transacao.createdAt || transacao.data_hora)}</div>
              <div className="text-slate-500 text-xs">{formatarDataRelativa(transacao.createdAt || transacao.data_hora)}</div>
            </div>
          </div>

        </div>

      </div>
    </motion.div>
  );
});

TransactionCard.displayName = 'TransactionCard';

// 5. Hook personalizado para estatísticas
const useEstatisticasTransacoes = (transacoes: Transacao[] | null) => {
  return useMemo(() => {
    if (!transacoes) return null;

    const total = transacoes.length;
    const creditos = transacoes.filter(t => t.tipo_operacao === 'credito').length;
    const debitos = transacoes.filter(t => t.tipo_operacao === 'debito').length;
    const valorTotal = transacoes.reduce((sum, t) => sum + (t.valor || 0), 0);
    const valorCreditos = transacoes.filter(t => t.tipo_operacao === 'credito').reduce((sum, t) => sum + (t.valor || 0), 0);
    const valorDebitos = transacoes.filter(t => t.tipo_operacao === 'debito').reduce((sum, t) => sum + (t.valor || 0), 0);

    // Transações recentes (últimos 7 dias)
    const umaSemanaAtras = new Date();
    umaSemanaAtras.setDate(umaSemanaAtras.getDate() - 7);
    const transacoesRecentes = transacoes.filter(t => {
      const data = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt || t.data_hora);
      return data > umaSemanaAtras;
    });

    return {
      total,
      creditos,
      debitos,
      valorTotal,
      valorCreditos,
      valorDebitos,
      transacoesRecentes: transacoesRecentes.length
    };
  }, [transacoes]);
};

// 6. Componente de Ordenação
const OrdenacaoTransacoes: React.FC<{
  ordenacao: string;
  onOrdenacaoChange: (ordenacao: string) => void;
}> = ({ ordenacao, onOrdenacaoChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const opcaoAtual = OPCOES_ORDENACAO.find(opcao => opcao.value === ordenacao) || OPCOES_ORDENACAO[0];

  const handleSelect = useCallback((value: string) => {
    onOrdenacaoChange(value);
    setIsOpen(false);
  }, [onOrdenacaoChange]);

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1, duration: 0.5 }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] text-white hover:bg-background transition-all duration-300 backdrop-blur-sm"
      >
        <FaSort className="w-4 h-4" />
        <span className="font-semibold">Ordenar por:</span>
        <span className="flex items-center gap-2">
          <opcaoAtual.icon className="w-4 h-4" />
          {opcaoAtual.label}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <FaSortDown className="w-4 h-4" />
        </motion.span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full right-0 mt-2 w-64 border border-primary/20 rounded-xl backdrop-blur-xl shadow-2xl z-50 overflow-hidden"
          >
            <div className="p-2">
              {OPCOES_ORDENACAO.map((opcao) => (
                <button
                  key={opcao.value}
                  onClick={() => handleSelect(opcao.value)}
                  className={`flex items-center gap-4 mt-2 w-full px-4 py-3 bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] rounded-xl text-left transition-all duration-200 ${ordenacao === opcao.value
                    ? 'bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]/20 text-white'
                    : 'text-white hover:bg-background'
                    }`}
                >
                  <opcao.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">{opcao.label}</span>
                  {ordenacao === opcao.value && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-2 h-2 bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] rounded-full"
                    />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// 7. Componente de Filtros separado
const FiltrosTransacoes: React.FC<{
  filtroAtivo: string;
  onFiltroChange: (filtro: 'all' | 'credito' | 'debito') => void;
  estatisticas: any;
}> = ({ filtroAtivo, onFiltroChange, estatisticas }) => {
  const filtros = [
    {
      key: 'all' as const,
      label: 'Todas',
      icon: <FaFilter className="w-3 h-3" />,
      count: estatisticas?.total || 0
    },
    {
      key: 'credito' as const,
      label: 'Entradas',
      icon: <FaArrowUp className="w-3 h-3" />,
      count: estatisticas?.creditos || 0
    },
    {
      key: 'debito' as const,
      label: 'Saídas',
      icon: <FaArrowDown className="w-3 h-3" />,
      count: estatisticas?.debitos || 0
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8, duration: 0.6 }}
      className="flex justify-center mb-8"
    >

      <div className="rounded-2xl p-2 border border-primary/20 backdrop-blur-sm">
        <div className="flex gap-2 flex-wrap justify-center">
          {filtros.map((filtro) => (
            <button
              key={filtro.key}
              onClick={() => onFiltroChange(filtro.key)}
              className={`flex items-center bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] text-white gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 relative border ${filtroAtivo === filtro.key
                ? 'bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]/20 text-white shadow-lg border-primary/20'
                : 'text-slate-300 hover:text-white hover:bg-background border-transparent'
                }`}
            >
              {filtro.icon}
              <span>{filtro.label}</span>
              <span className={`px-2 py-1 rounded-full text-xs ${filtroAtivo === filtro.key
                ? 'bg-white/20 text-white'
                : 'bg-background text-slate-300'
                }`}>
                {filtro.count}
              </span>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// 8. Componente de Estatísticas separado
const EstatisticasTransacoes: React.FC<{ estatisticas: any }> = ({ estatisticas }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.6, duration: 0.6 }}
    className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
  >
    <div className="rounded-xl p-4 border border-primary/20/40 backdrop-blur-sm">
      <div className="text-2xl font-black text-white mb-1">{estatisticas.total}</div>
      <div className="text-white text-sm">Total Transações</div>
    </div>
    <div className="rounded-xl p-4 border border-primary/20/40 backdrop-blur-sm">
      <div className="text-2xl font-black text-white mb-1">{estatisticas.creditos}</div>
      <div className="text-white text-sm">Entradas</div>
      <div className="text-white text-sm font-bold mt-1">+{formatarMoeda(estatisticas.valorCreditos).replace('R$', 'TC')}</div>
    </div>
    <div className=" rounded-xl p-4 border border-primary/20/40 backdrop-blur-sm">
      <div className="text-2xl font-black text-white mb-1">{estatisticas.debitos}</div>
      <div className="text-white text-sm">Saídas</div>
      <div className="text-white text-sm font-bold mt-1">-{formatarMoeda(estatisticas.valorDebitos).replace('R$', 'TC')}</div>
    </div>
    <div className=" rounded-xl p-4 border border-primary/20/40 backdrop-blur-sm">
      <div className="text-2xl font-black text-white mb-1">{estatisticas.transacoesRecentes}</div>
      <div className="text-white text-sm">Últimos 7 dias</div>
      <div className="text-white text-xs mt-1">Transações recentes</div>
    </div>
  </motion.div>
);

// 9. Componente Principal otimizado
const TransactionHistoryPage: React.FC = () => {
  const { usuario } = useAuth();
  const clienteId = usuario?.id;
  const [filtroAtivo, setFiltroAtivo] = useState<'all' | 'credito' | 'debito'>('all');
  const [ordenacao, setOrdenacao] = useState<string>('data-desc');

  const {
    dados: transacoes,
    carregando,
    erro,
  } = useFirestoreQuery<Transacao>(
    'transacoes',
    clienteId ? [{ campo: 'userId', operador: '==', valor: clienteId }] : null,
    { campo: 'criado_em', direcao: 'desc' },
    null,
    !!clienteId
  );

  const estatisticas = useEstatisticasTransacoes(transacoes);

  // 10. Função de ordenação
  const transacoesOrdenadasEFiltradas = useMemo(() => {
    if (!transacoes) return [];

    // Aplicar filtro
    let transacoesFiltradas = transacoes;
    if (filtroAtivo !== 'all') {
      transacoesFiltradas = transacoes.filter(t => t.tipo_operacao === filtroAtivo);
    }

    // Aplicar ordenação
    return [...transacoesFiltradas].sort((a, b) => {
      const [campo, direcao] = ordenacao.split('-') as [OrdenacaoCampo, OrdenacaoDirecao];
      const multiplicador = direcao === 'desc' ? -1 : 1;

      switch (campo) {
        case 'data': {
          const dataA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || a.data_hora);
          const dataB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || b.data_hora);
          return (dataA.getTime() - dataB.getTime()) * multiplicador;
        }

        case 'valor': {
          const valorA = a.valor || 0;
          const valorB = b.valor || 0;
          return (valorA - valorB) * multiplicador;
        }

        case 'tipo': {
          const tipoA = a.tipo || a.tipo_operacao || '';
          const tipoB = b.tipo || b.tipo_operacao || '';
          return tipoA.localeCompare(tipoB) * multiplicador;
        }

        default:
          return 0;
      }
    });
  }, [transacoes, filtroAtivo, ordenacao]);

  const handleFiltroChange = useCallback((filtro: 'all' | 'credito' | 'debito') => {
    setFiltroAtivo(filtro);
  }, []);

  const handleOrdenacaoChange = useCallback((novaOrdenacao: string) => {
    setOrdenacao(novaOrdenacao);
  }, []);

  // 11. Estado vazio otimizado
  const estadoVazio = useMemo(() => {
    if (carregando) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Spinner size="lg" color="primary" />
            <p className="text-slate-400 mt-4">Carregando suas transações...</p>
          </div>
        </div>
      );
    }

    if (erro) {
      return (
        <Alert
          tipo="error"
          mensagem={`Erro ao carregar histórico: ${erro.message}`}
        />
      );
    }

    if (!transacoesOrdenadasEFiltradas || transacoesOrdenadasEFiltradas.length === 0) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center py-20"
        >
          <div className="w-32 h-32 rounded-3xl flex items-center justify-center mb-6 shadow-2xl border border-primary/20">
            <span className="text-4xl">💸</span>
          </div>
          <h3 className="text-2xl font-bold text-white mb-3 text-center">Nenhuma transação encontrada</h3>
          <p className="text-slate-400 text-center text-lg max-w-md">
            {filtroAtivo === 'all'
              ? 'Suas transações aparecerão aqui quando você fizer recargas ou pagamentos.'
              : `Nenhuma transação do tipo ${filtroAtivo === 'credito' ? 'entrada' : 'saída'} encontrada.`
            }
          </p>
        </motion.div>
      );
    }

    return null;
  }, [carregando, erro, transacoesOrdenadasEFiltradas, filtroAtivo]);

  return (
    <ClientLayout>
      <div className="fixed inset-0 my-20 z-0">
        <img
          src="/BackgroundHistorico.png"
          alt="Background"
          className="w-full h-screen object-cover"
        />
        <div className="absolute inset-0 bg-black/10"></div>
      </div>
      <div className="relative z-10 min-h-screen">
        {/* Background otimizado */}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="container mx-auto px-4 py-8"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-4xl md:text-5xl lg:text-6xl text-white font-bold bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent mb-4"
            >
              Histórico de Transações
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto"
            >
              Acompanhe todas as suas movimentações de Tugas Coins em tempo real
            </motion.p>
          </div>

          {/* Estatísticas */}
          {estatisticas && <EstatisticasTransacoes estatisticas={estatisticas} />}

          {/* Controles: Filtros e Ordenação */}
          {estatisticas && estatisticas.total > 0 && (
            <div className="flex flex-col lg:flex-row justify-between items-center gap-4 mb-8">
              <FiltrosTransacoes
                filtroAtivo={filtroAtivo}
                onFiltroChange={handleFiltroChange}
                estatisticas={estatisticas}
              />
              <OrdenacaoTransacoes
                ordenacao={ordenacao}
                onOrdenacaoChange={handleOrdenacaoChange}
              />
            </div>
          )}

          {/* Conteúdo */}
          {estadoVazio || (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="space-y-6"
            >
              <div className="text-slate-400 text-sm text-center">
                Mostrando {transacoesOrdenadasEFiltradas.length} transação{transacoesOrdenadasEFiltradas.length !== 1 ? 'es' : ''}
                {filtroAtivo !== 'all' && ` (${filtroAtivo === 'credito' ? 'entradas' : 'saídas'})`}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                <AnimatePresence>
                  {transacoesOrdenadasEFiltradas.map((transacao, index) => (
                    <TransactionCard
                      key={transacao.id || `transacao-${index}`}
                      transacao={transacao}
                      index={index}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </ClientLayout>
  );
};

export default TransactionHistoryPage;